import { ChevronDown, FastForward, UserRound } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "@/components/common/loader";
import { getPipeline, rollForward, updateStage } from "@/features/pipeline/api";
import {
  PIPELINE_STATUS_META,
  PIPELINE_STATUS_OPTIONS,
  type PipelineStage,
  type PipelineStatus,
} from "@/features/pipeline/types";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

function errText(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

/** A single stage row: ordinal, label, status badge + inline status Select, a
 * read-only assignee label, and an expandable notes editor that PATCHes on save. */
function StageRow({
  stage,
  busy,
  onStatusChange,
  onNotesSave,
}: {
  stage: PipelineStage;
  busy: boolean;
  onStatusChange: (status: PipelineStatus) => void;
  onNotesSave: (notes: string) => Promise<boolean>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(stage.notes);
  const [saving, setSaving] = useState(false);
  const meta = PIPELINE_STATUS_META[stage.status];

  // Keep the local draft in sync when the stage's notes change under us (e.g.
  // after a successful save elsewhere) without stomping an in-progress edit.
  useEffect(() => {
    if (!expanded) setDraft(stage.notes);
  }, [stage.notes, expanded]);

  async function handleSave() {
    if (draft === stage.notes) {
      setExpanded(false);
      return;
    }
    setSaving(true);
    const ok = await onNotesSave(draft);
    setSaving(false);
    if (ok) setExpanded(false);
  }

  return (
    <div className="flex flex-col gap-3 px-4 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cn(
            "grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold",
            stage.status === "DONE"
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground",
          )}
        >
          {stage.position}
        </span>
        <span className="text-foreground min-w-0 flex-1 text-sm font-semibold">
          {stage.stageLabel}
        </span>
        <Badge variant={meta.variant}>{meta.label}</Badge>
        <Select
          value={stage.status}
          disabled={busy}
          onValueChange={(v) => onStatusChange(v as PipelineStatus)}
        >
          <SelectTrigger
            size="sm"
            className="w-36"
            aria-label={`Status for ${stage.stageLabel}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PIPELINE_STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {PIPELINE_STATUS_META[s].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          aria-expanded={expanded}
          aria-label={`Notes for ${stage.stageLabel}`}
          onClick={() => setExpanded((e) => !e)}
        >
          <ChevronDown className={cn("size-4 transition-transform", expanded && "rotate-180")} />
          Notes
        </Button>
      </div>

      <div className="text-muted-foreground flex items-center gap-1.5 pl-10 text-xs">
        <UserRound className="size-3.5" />
        {stage.assigneeName || "Unassigned"}
      </div>

      {expanded && (
        <div className="grid gap-2 pl-10">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a note for this stage…"
            aria-label={`Notes text for ${stage.stageLabel}`}
            disabled={saving}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={() => {
                setDraft(stage.notes);
                setExpanded(false);
              }}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={saving} onClick={handleSave}>
              {saving ? "Saving…" : "Save notes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function PipelineTracker({ caseId }: { caseId: string }) {
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Which stage's PATCH is in flight (disables that row's Select).
  const [pendingStageId, setPendingStageId] = useState<string | null>(null);
  const [confirmRoll, setConfirmRoll] = useState(false);
  const [rolling, setRolling] = useState(false);

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    getPipeline(caseId)
      .then((rows) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setStages(rows);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setStages([]);
        setError(errText(err, "Failed to load the pipeline."));
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleStatusChange(stage: PipelineStage, status: PipelineStatus) {
    if (status === stage.status || pendingStageId === stage.id) return;
    setPendingStageId(stage.id);
    try {
      const updated = await updateStage(caseId, stage.stage, { status });
      if (!mountedRef.current) return;
      setStages((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      toast.success(`${updated.stageLabel}: ${PIPELINE_STATUS_META[status].label}`);
    } catch (err) {
      if (!mountedRef.current) return;
      toast.error(errText(err, "Couldn't update the stage."));
    } finally {
      if (mountedRef.current) setPendingStageId(null);
    }
  }

  /** Returns true when the save landed, so the row can collapse. */
  async function handleNotesSave(stage: PipelineStage, notes: string): Promise<boolean> {
    try {
      const updated = await updateStage(caseId, stage.stage, { notes });
      if (!mountedRef.current) return false;
      setStages((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      toast.success("Notes saved");
      return true;
    } catch (err) {
      if (!mountedRef.current) return false;
      toast.error(errText(err, "Couldn't save the notes."));
      return false;
    }
  }

  async function handleRollForward() {
    if (rolling) return;
    setRolling(true);
    try {
      const result = await rollForward(caseId);
      if (!mountedRef.current) return;
      toast.success(`Next-year case opened (${result.taxYears || "new tax year"})`);
    } catch (err) {
      if (!mountedRef.current) return;
      // Permission failures (403) surface the backend's message here.
      toast.error(errText(err, "Couldn't roll forward to next year."));
    } finally {
      if (mountedRef.current) {
        setRolling(false);
        setConfirmRoll(false);
      }
    }
  }

  if (loading) return <Loader className="mt-6" />;

  if (error) {
    return (
      <div className="grid gap-3 py-10 text-center">
        <p className="text-destructive text-sm">{error}</p>
        <div>
          <Button variant="outline" size="sm" onClick={load}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <p className="text-muted-foreground py-10 text-center text-sm">
        No pipeline stages for this case yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          Work this case through all {stages.length} stages. Change a stage&apos;s status or
          add notes as you go.
        </p>
        <Button variant="outline" size="sm" onClick={() => setConfirmRoll(true)}>
          <FastForward className="size-4" /> Roll forward to next year
        </Button>
      </div>

      <Card className="gap-0 divide-y overflow-hidden p-0">
        {stages.map((stage) => (
          <StageRow
            key={stage.id}
            stage={stage}
            busy={pendingStageId === stage.id}
            onStatusChange={(status) => handleStatusChange(stage, status)}
            onNotesSave={(notes) => handleNotesSave(stage, notes)}
          />
        ))}
      </Card>

      <AlertDialog open={confirmRoll} onOpenChange={(open) => !open && setConfirmRoll(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Roll forward to next year?</AlertDialogTitle>
            <AlertDialogDescription>
              This opens a fresh case for the next tax year, carrying this case&apos;s setup
              forward. You need team-lead authority or higher to do this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rolling}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRollForward} disabled={rolling}>
              {rolling ? "Rolling forward…" : "Roll forward"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
